// @flow
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import * as DmsCommon from './../supports/dms-common';
import * as DmsService from './../supports/dms-service';
import { GlobalEvent, GlobalState } from './GlobalContext';
import { isOfficeFile, openOfficeFileInNewTab } from './FileViewer';
import MsgDialog from './MsgDialog';
import PropTypes from 'prop-types';
import DocSrcAddDialog from './DocSrcAddDialog';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from './ConfirmDialog';
import dayjs from "dayjs"

import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';

export default function DocSrcTab(props) {
  const { sysID, docDetail, afterUpdate, isFolderOffline } = props;

  // Get Global state and dispatcher
  const [gState, gStateDispatch] = React.useContext(GlobalState);

  const [gEvent, gEventDispatch] = React.useContext(GlobalEvent);

  const [showLoading, setShowLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const onCatchPromiseError = (e) => {
    gEventDispatch({
      showErr: true,
      err: e,
    });

  }


  const sources = docDetail.SrcList || [];

  const gridData = sources.map(item => {
    return {
      ...item,
      FileSizeDisplay: DmsCommon.getReadableFileSizeString(item.FileSize),
      UploadDateJs: dayjs(item.UploadDate),
      UploadDateDisplay: dayjs(item.UploadDate).format(DmsCommon.SHORT_DATE_FORMAT),
    }
  })


  const [openAddSrcDialog, setOpenAddSrcDialog] = useState(false);

  const handleClickAdd = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }

    if (docDetail.Permission < DmsCommon.AccessRightEnum.arModify) {
      setErrMsg("Can't upload document source. Access denied!");
      setShowError(true);
      return
    }

    setOpenAddSrcDialog(true);
  }

  const handleAfterUpdate = () => {
    setOpenAddSrcDialog(false);
    afterUpdate && afterUpdate();
  }



  const [selectedRecs, setSelectedRecs] = useState([]);
  const onSelectionChanged = (params) => {
    let nodes = params.api.getSelectedNodes();

    let selRecs = nodes.map(item => {
      return item.data
    });

    setSelectedRecs(selRecs);

  }

  const handleOpenClick = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }

    if (docDetail.Permission < DmsCommon.AccessRightEnum.arReadDoc) {
      setErrMsg("Can't open file. Access denied!");
      setShowError(true);
      return
    }

    if (selectedRecs.length == 1) {

      const selRec = selectedRecs[0];
      // console.log(selRec);

      const vFileExt = DmsCommon.getFileExtension(selRec.FileName);

      if (isOfficeFile(selRec.FileName)) {
        const vDownloadURL = DmsService.getDownloadUrlWithFilename(gState.sessionID, sysID, selRec.DocID, null, selRec.SrcNo, null, null, 0, selRec.FileName);
        openOfficeFileInNewTab({
          url: vDownloadURL,
          filename: selRec.FileName,
          title: `${docDetail?.Subject || selRec.FileName} (Source: ${selRec.FileName})`,
          canDownload: docDetail?.Permission >= DmsCommon.AccessRightEnum.arReadDn,
        });
      } else {
        const vIsAttach = (vFileExt == '.pdf' || vFileExt == '.txt') ? 0 : 1;
        const vDownloadURL = DmsService.getDownloadUrl(gState.sessionID, sysID, selRec.DocID, null, selRec.SrcNo, null, null, vIsAttach);
        window.open(vDownloadURL, '_blank');
      }

      // Noted: According to DMSWin32, It is intended to log as download instead of open
      DmsService.accessLogPromise(gState.sessionID, sysID, DmsCommon.DmsLogEnum.daSrcDownload, `${selRec.DocID}`);

    }


  }


  const handleDownloadClick = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }

    if (docDetail.Permission < DmsCommon.AccessRightEnum.arReadDn) {
      setErrMsg("Can't download file. Access denied!");
      setShowError(true);
      return
    }
    // console.log("docDetail", docDetail);
    // console.log("selectedRecs", selectedRecs);


    if (selectedRecs.length == 1) {
      const selRec = selectedRecs[0];

      DmsService.downloadFileStream(
        gState.sessionID,
        sysID,
        selRec.DocID,
        null,
        selRec.SrcNo,
        null,
        null,
        selRec.FileName,
        () => setShowLoading(true),
        () => setShowLoading(false),
        (err) => {
          setErrMsg(err);
          setShowError(true);
        }
      );

      // Noted: According to DMSWin32, It is intended to log as download instead of open
      DmsService.accessLogPromise(gState.sessionID, sysID, DmsCommon.DmsLogEnum.daSrcDownload, `${selRec.DocID}`);

      afterUpdate && afterUpdate();
    }


  }


  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleDelClick = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }

    // console.log("docDetail", docDetail);
    // console.log("selectedRecs", selectedRecs);


    if (selectedRecs.length == 1) {

      if (docDetail.Permission < DmsCommon.AccessRightEnum.arModifyDel) {
        setErrMsg("Can't delete file. Insufficient permission!");
        setShowError(true);
        return
      }

      setConfirmText("Delete the selected file?");
      setOpenConfirmDialog(true);

    }


  }

  const handleConfirmDeleteClick = () => {
    if (selectedRecs.length == 1) {
      const selRec = selectedRecs[0];

      setShowLoading(true);
      DmsService.docSrcDelPromise(gState.sessionID, sysID, selRec.DocID, selRec.SrcNo)
        .then(() => {
          setShowLoading(false);

          afterUpdate && afterUpdate();

        })
        .catch(e => {
          setShowLoading(false);
          onCatchPromiseError(e);
        })
    }
  }

  const cellStyle = { paddingLeft: '10px', paddingRight: '10px', fontSize: '0.8rem' };

  const comparatorFileSize = (a, b, nodeA, nodeB) => {
    return nodeA.data.FileSize - nodeB.data.FileSize;
  }

  const comparatorUploadDate = (a, b, nodeA, nodeB) => {

    const [aDate, bDate] = [nodeA.data.UploadDateJs, nodeB.data.UploadDateJs];
    if (aDate.isBefore(bDate)) {
      return -1;
    } else if (aDate.isAfter(bDate)) {
      return 1;
    } else {
      return 0;
    }
  }


  const compareStrInsensitive = (aStrA, aStrB) => {
    return aStrA.localeCompare(aStrB, undefined, { sensitivity: "base" })
  }

  const comparatorRemark = (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data.Remark, nodeB.data.Remark)
  }

  const comparatorFileName = (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data.FileName, nodeB.data.FileName)
  }

  const comparatorUploadUserName = (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data.UploadUserName, nodeB.data.UploadUserName)
  }


  const columnDefs = [
    { field: 'Remark', headerName: "Remarks", cellStyle: cellStyle, width: 120, autoHeight: true, wrapText: true, comparator: comparatorRemark },
    { field: 'FileName', headerName: "FileName", cellStyle: cellStyle, minWidth: 150, flex: "1", autoHeight: true, wrapText: true, comparator: comparatorFileName },
    { field: 'FileSizeDisplay', headerName: "File Size", cellStyle: cellStyle, width: 120, comparator: comparatorFileSize },
    { field: 'UploadUserName', headerName: "Uploaded By", cellStyle: cellStyle, width: 150, comparator: comparatorUploadUserName },
    { field: 'UploadDateDisplay', headerName: "Upload Date", cellStyle: cellStyle, comparator: comparatorUploadDate },
  ];


  return (
    <Box sx={{ display: 'flex', height: "70vh", flexDirection: 'column' }} >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MsgDialog
        open={showError}
        title="Error"
        msgText={errMsg}
        onClose={() => setShowError(false)}
      />


      <Box sx={{ py: 1, px: 1 }} >

        <Button startIcon={<AddIcon />} onClick={handleClickAdd}  >
          Add
        </Button>
        <DocSrcAddDialog
          sysID={sysID}
          open={openAddSrcDialog}
          onClose={() => setOpenAddSrcDialog(false)}
          docDetail={docDetail}
          afterUpdate={handleAfterUpdate}
        />

        <Button disabled={selectedRecs.length == 0} startIcon={<OpenInNewIcon />} onClick={handleOpenClick} >
          Open
        </Button>

        <Button disabled={selectedRecs.length == 0} startIcon={<CloudDownloadIcon />} onClick={handleDownloadClick}  >
          Download
        </Button>

        <Button disabled={selectedRecs.length == 0} startIcon={<DeleteIcon />} onClick={handleDelClick} >
          Delete
        </Button>
        <ConfirmDialog
          open={openConfirmDialog}
          text={confirmText}
          confirmBtnCaption='Delete'
          onCancelClick={() => {
            setOpenConfirmDialog(false);
          }}
          onConfirmClick={() => {
            setOpenConfirmDialog(false);
            handleConfirmDeleteClick();
          }}
        />
      </Box>

      <div className="ag-theme-material" style={{ width: '100%', height: '100%', bgcolor: 'yellow' }}>
        <AgGridReact

          rowData={gridData}

          defaultColDef={{
            width: 100,
            resizable: true,
            sortable: true,
            suppressMovable: true,
          }}
          columnDefs={columnDefs}
          headerHeight={40}

          suppressNoRowsOverlay

          rowSelection="single"
          onSelectionChanged={onSelectionChanged}
        >


        </AgGridReact>

      </div>

    </Box>
    // <Box sx={{ flexGrow: 1 }}>
    //     <div style={{ width: '100%', height: '100%' }}>

    //     </div>
    // </Box>


  );
}

DocSrcTab.propTypes = {
  sysID: PropTypes.string,
  docDetail: PropTypes.object,
  afterUpdate: PropTypes.func,
  isFolderOffline: PropTypes.bool,
};
